<script>
import axios from '@axios'
import { BFormGroup, BFormRadioGroup } from 'bootstrap-vue'
import Overlay from "@core/components/common/Overlay.vue";

export default {
    components: { BFormGroup, BFormRadioGroup, Overlay },
    props: {
        readOnly: {
            type: Boolean,
            default: false,
        },
        value: {
            default: null,
        },
        config: {
            type: Object,
            default() {
                return {
                    properties: {},
                };
            }
        },
        list: {
            type: Object,
            default: null,
        },
        substance: {
            type: Object,
            default: null,
        }
    },
    data() {
        return {
            responseData: {},
            isLoading: false,
            componentAnswer: this.value === true || this.value === false ? this.value : null,
        };
    },
    computed: {
        propsConfig() {
            return this.config && this.config.properties ? this.config.properties : {};
        },
        smlLabel() {
            return this.propsConfig.smlLabel ? this.$t(this.propsConfig.smlLabel) : '';
        },
        restrictionsLabel() {
            return this.propsConfig.restrictionsLabel ? this.$t(this.propsConfig.restrictionsLabel) : '';
        },
        questionLabel() {
            return this.propsConfig.questionLabel ? this.$t(this.propsConfig.questionLabel) : '';
        },
        required() {
            return this.propsConfig.required === true;
        },
        optionsUrlTemplate() {
            return this.propsConfig.optionsUrlTemplate || '';
        },
        optionsUrlParams() {
            return this.propsConfig.optionsUrlParams || {};
        },
        urlPlaceholdersResolved() {
            return Object.keys(this.optionsUrlParams).every((placeholderKey) => {
                const sourceKey = this.optionsUrlParams[placeholderKey];
                return this.resolveContextValue(sourceKey) !== null;
            });
        },
        optionsUrl() {
            if (!this.optionsUrlTemplate || !this.urlPlaceholdersResolved) {
                return null;
            }

            let url = this.optionsUrlTemplate;
            Object.keys(this.optionsUrlParams).forEach((placeholderKey) => {
                const sourceKey = this.optionsUrlParams[placeholderKey];
                const value = this.resolveContextValue(sourceKey);
                url = url.replace(`{${placeholderKey}}`, encodeURIComponent(value));
            });

            return url;
        },
        radioOptions() {
            return [
                { text: this.$t('Component(Choice):yes'), value: true },
                { text: this.$t('Component(Choice):no'), value: false },
            ];
        },
        hasSml() {
            return this.responseData && this.responseData.sml && this.responseData.sml.value !== undefined && this.responseData.sml.value !== null;
        },
        smlText() {
            if (!this.hasSml) {
                return '';
            }

            const unit = this.responseData.sml.unit || '';
            return `${this.responseData.sml.value}${unit ? ` ${unit}` : ''}`;
        },
        restrictions() {
            return Array.isArray(this.responseData.restrictions) ? this.responseData.restrictions : [];
        },
        hasRestrictions() {
            return this.restrictions.length > 0;
        },
        answerValue() {
            if (this.componentAnswer === true || this.componentAnswer === false) {
                return this.componentAnswer;
            }
            return null;
        },
        mandatoryClass() {
            return this.required && this.answerValue === null ? 'input-bg-warning' : '';
        },
        readOnlyAnswerText() {
            if (this.answerValue === true) {
                return this.$t('Component(Choice):yes');
            }
            if (this.answerValue === false) {
                return this.$t('Component(Choice):no');
            }
            return '-';
        }
    },
    created() {
        this.loadData();
    },
    watch: {
        value(newValue) {
            this.componentAnswer = newValue === true || newValue === false ? newValue : null;
        },
        optionsUrl() {
            this.loadData();
        }
    },
    methods: {
        resolveContextValue(sourceKey) {
            if (sourceKey === 'listId') {
                return this.list && this.list.id ? this.list.id : null;
            }
            if (sourceKey === 'substanceId') {
                return this.substance && this.substance.id ? this.substance.id : null;
            }
            return null;
        },
        onAnswerChange(value) {
            this.componentAnswer = value;
            this.$emit('input', value);
        },
        async loadData() {
            this.responseData = {};

            if (!this.optionsUrl) {
                return;
            }

            this.isLoading = true;
            try {
                const response = await axios.get(this.optionsUrl);
                this.responseData = response && response.data && typeof response.data === 'object'
                    ? response.data
                    : {};
            } finally {
                this.isLoading = false;
            }
        }
    }
}
</script>

<template>
    <b-form-group>
        <Overlay :show="isLoading">
            <div v-if="hasSml" class="mb-1">
                <h6>{{ smlLabel }}</h6>
                <div>{{ smlText }}</div>
            </div>

            <div v-if="hasRestrictions" class="mb-1">
                <h6>{{ restrictionsLabel }}</h6>
                <div v-for="restriction in restrictions" :key="restriction.id">
                    {{ restriction.title }}
                </div>
            </div>

            <div :class="mandatoryClass" class="p-50 rounded">
                <div class="d-flex align-items-center">
                    <div>{{ questionLabel }}</div>
                    <div v-if="readOnly" class="ml-1"><strong>{{ readOnlyAnswerText }}</strong></div>
                    <b-form-radio-group
                        v-else
                        :name="`fcm-requirements-check-${config.type}-${list ? list.id : 'common'}`"
                        :checked="answerValue"
                        :options="radioOptions"
                        size="sm"
                        buttons
                        button-variant="outline-primary"
                        class="mb-0 ml-1"
                        @change="onAnswerChange"
                    />
                </div>
            </div>
        </Overlay>
    </b-form-group>
</template>
